import React, { useEffect, useReducer, useRef, useState } from "react";

import { ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";
import { Input, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Endpoint from "../../../../services/api/endpoint";
import { getInitialState } from "../../../../state";
import { contractorReducer } from "../../../../state/reducers";
import { IApplicationState } from "../../../../store";
import { handleChange } from "../../../../utils/editorUtils";
import { IAddress, IContractor, IUser } from "../../../../utils/types/models";
import { emailRegex, isNullOrEmpty, phoneRegex } from "../../../../utils/utils";
import EntityEditor, { AddressEdit, IEditorProps, IEditScreenProps, YesNoSwitch } from "../../../global/editor";

const ContractorEditor: React.FC<IEditorProps<IContractor>> = (props: IEditorProps<IContractor>) => {
	const { t } = useTranslation();
	const [contractorState, dispatch] = useReducer(contractorReducer, getInitialState<IContractor>());
	const currentUser: IUser = useSelector((state: IApplicationState) => state.authenticationState.currentUser);
	const initialContractor: IContractor = {
		id: (props as IEditScreenProps<IContractor>).recordId || 0,
		name: "",
		address: {
			street: "",
			number: "",
			postalCode: "",
			municipality: "",
			province: "",
			country: "",
			fullAddress: ""
		},
		contactName: "",
		phone: "",
		email: "",
		active: true,
		companyId: currentUser.currentCompanyId
	};
	const [contractor, setContractor] = useState<IContractor>(initialContractor);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (contractorState.entity) {
			setContractor(contractorState.entity);
		}
	}, [contractorState.entity]);

	function onChange(event: React.ChangeEvent<HTMLInputElement> | SwitchChangeEvent | ComboBoxChangeEvent): void {
		setContractor(handleChange(contractor, event));
		setDataChanged(true);
	}

	function setAddress(address: IAddress): void {
		const newContractor: IContractor = { ...contractor };
		newContractor.address = address;
		setContractor(newContractor);
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(contractor.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		if (isNullOrEmpty(contractor.address.street)) {
			messages.push(t("fill_in_required_field", { field: t("street").toLowerCase() }));
		}
		if (isNullOrEmpty(contractor.address.number)) {
			messages.push(t("fill_in_required_field", { field: t("number").toLowerCase() }));
		}
		if (isNullOrEmpty(contractor.address.postalCode)) {
			messages.push(t("fill_in_required_field", { field: t("postalCode").toLowerCase() }));
		}
		if (isNullOrEmpty(contractor.address.municipality)) {
			messages.push(t("fill_in_required_field", { field: t("municipality").toLowerCase() }));
		}
		if (isNullOrEmpty(contractor.address.country)) {
			messages.push(t("fill_in_required_field", { field: t("country").toLowerCase() }));
		}
		if (!isNullOrEmpty(contractor.phone) && !contractor.phone.match(phoneRegex)) {
			messages.push(t("fill_in_correct_format", { field: t("phone").toLowerCase(), example: "012345678' " + t("or") + " '0476123456" }));
		}
		if (!isNullOrEmpty(contractor.email) && !contractor.email.match(emailRegex)) {
			messages.push(t("fill_in_correct_format", { field: t("email").toLowerCase(), example: "user@test.com" }));
		}
		return messages;
	}

	const readonly: boolean = (props as IEditScreenProps<IContractor>).readonly;
	return (
		<EntityEditor
			width="70%"
			record={contractor}
			endpoint={Endpoint.Contractors}
			entityState={contractorState}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={contractor.name}
			entityType={t("contractor")}
			close={props.close}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} className="full-width-field" value={contractor.name} onChange={onChange} required disabled={readonly} />
				</label>
				<label className="k-form-field">
					<span>{t("vatNumber")}</span>
					<Input name="vatNumber" className="full-width-field" value={contractor.vatNumber} onChange={onChange} disabled={readonly} />
				</label>
				<AddressEdit address={contractor.address} addressField="address" onChange={onChange} setAddress={setAddress} required readonly={readonly} />
				<div className="row">
					<div className="col">
						<label className="k-form-field">
							<span>{t("contactName")} *</span>
							<Input name="contactName" className="full-width-field" value={contractor.contactName} onChange={onChange} required disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("phone")}</span>
							<Input name="phone" className="full-width-field" value={contractor.phone} onChange={onChange} pattern={phoneRegex.source} disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("email")}</span>
							<Input name="email" type="email" className="full-width-field" value={contractor.email} onChange={onChange} disabled={readonly} />
						</label>
					</div>
				</div>
				{!props.hideActive && (
					<div className="k-form-field">
						<span>{t("active")}</span>
						<YesNoSwitch name="active" checked={contractor.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default ContractorEditor;